import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { MessageCircle } from 'lucide-react';
import { logEvent } from '../lib/amplitude';

// Asked once per account, right after "Not now" on the download wall: the one moment we can
// hear why someone who wanted the file didn't pay for it. A row of taps, nothing blocking,
// and the next download brings the plain wall back exactly as before.
const REASONS = [
  ['too_expensive', 'Too expensive'],
  ['one_file_enough', 'One file was enough'],
  ['result_not_good', "The result wasn't good enough"],
  ['just_looking', 'Just looking'],
];

const askedKey = (userId) => `midimate_wall_feedback_asked_${userId}`;

export function wallFeedbackAsked(userId) {
  try {
    return !!localStorage.getItem(askedKey(userId));
  } catch (e) {
    return true; // no storage: don't risk asking on every visit
  }
}

export function markWallFeedbackAsked(userId) {
  try {
    localStorage.setItem(askedKey(userId), '1');
  } catch (e) {
    // ignore
  }
}

const WallFeedback = ({ user, source, onDone }) => {
  const [reason, setReason] = useState(null);
  const [note, setNote] = useState('');
  const [sent, setSent] = useState(false);

  const send = async (chosen, text) => {
    setSent(true);
    logEvent('Wall Feedback', { source, reason: chosen, hasNote: !!text });
    const { error } = await supabase.from('wall_feedback').insert({
      user_id: user.id,
      source,
      reason: chosen,
      note: text ? text.slice(0, 500) : null,
    });
    if (error) console.error('wall_feedback', error);
    setTimeout(onDone, 1500);
  };

  const pick = (chosen) => {
    setReason(chosen);
    if (chosen !== 'other') send(chosen, note.trim());
  };

  return (
    <div className="flex justify-start">
      <div className="flex items-start space-x-3 max-w-[85%] sm:max-w-[75%] md:max-w-[600px]">
        <div className="flex-shrink-0 rounded-full p-2 bg-muted">
          <MessageCircle className="h-5 w-5" />
        </div>
        <div className="rounded-lg p-4 bg-muted/60 border border-border">
          {sent ? (
            <p className="text-sm">Thanks, that helps.</p>
          ) : (
            <>
              <p className="text-sm">No problem. One tap, if you have a second: what stopped you?</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {REASONS.map(([key, label]) => (
                  <Button key={key} size="sm" variant={reason === key ? 'default' : 'outline'} onClick={() => pick(key)}>
                    {label}
                  </Button>
                ))}
                <Button size="sm" variant={reason === 'other' ? 'default' : 'outline'} onClick={() => setReason('other')}>
                  Something else
                </Button>
              </div>
              {reason === 'other' && (
                <form
                  className="mt-3 flex gap-2"
                  onSubmit={(e) => { e.preventDefault(); if (note.trim()) send('other', note.trim()); }}
                >
                  <Input
                    autoFocus
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    maxLength={500}
                    placeholder="Tell us in a few words"
                    className="h-8 text-sm"
                  />
                  <Button size="sm" type="submit" disabled={!note.trim()}>Send</Button>
                </form>
              )}
              <button type="button" className="mt-2 text-xs text-muted-foreground hover:underline" onClick={onDone}>
                Skip
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WallFeedback;
